import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import "./Sessions.scss";
import PopupReport from "../Popups/PopupReport";
import PopupSession from "../Popups/PopupSession";
import PopupDeleteSession from "../Popups/PopupDeleteSession";
import { toast } from "react-hot-toast";
import { MdSearch, MdFilterList, MdFormatListBulleted , MdVisibility, MdVisibilityOff, MdDragIndicator, MdDescription, MdAddCircleOutline, MdDelete } from "react-icons/md";
import { IoIosArrowBack, IoIosArrowForward  } from "react-icons/io";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

function RenderColumnFilter({ column }) {
  const filterValue = column.getFilterValue();
  const variant = column.columnDef.meta?.filterVariant;
  const options = column.columnDef.meta?.options || [];

  if (variant === 'range') {
    return (
      <div className="flex space-x-1">
        <input
          type="number"
          value={filterValue?.[0] ?? ''}
          onChange={e => column.setFilterValue([e.target.value, filterValue?.[1]])}
          placeholder="Min"
          className="column-filter-input"
        />
        <input
          type="number"
          value={filterValue?.[1] ?? ''}
          onChange={e => column.setFilterValue([filterValue?.[0], e.target.value])}
          placeholder="Max"
          className="column-filter-input"
        />
      </div>
    );
  }
  if (variant === 'select') {
    return (
      <select
        value={filterValue ?? ''}
        onChange={e => column.setFilterValue(e.target.value)}
        className="column-filter-input"
      >
        <option value="">All</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="text"
      className="column-filter-input"
      placeholder="Filter..."
      value={filterValue ?? ''}
      onChange={e => column.setFilterValue(e.target.value)}
      style={{ width: `${column.getSize()-5}px` }}
    />
  );
}

function IndeterminateCheckbox({ indeterminate, className = "", ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [indeterminate, rest.checked]);
  return (
    <label className="custom-checkbox">
      <input
        type="checkbox"
        ref={ref}
        className="checkbox-input"
        {...rest}
      />
      <span className="checkmark" />
    </label>  
  );
};

const DraggableColumnItem = ({ column, index, moveItem, alwaysVisible = false }) => {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: "column",
    hover(item) {
      if (!ref.current || item.index === index) return;
      moveItem(item.index, index);
      item.index = index;
    },
  });
  const [, drag] = useDrag({
    type: "column",
    item: { id: column.id, index },
    canDrag: !alwaysVisible,
  });
  drag(drop(ref));

  return (
    <div ref={ref} className="notion-prop-row">
      <div className="notion-prop-drag">
        {!alwaysVisible && <MdDragIndicator size={18} />}
      </div>
      <div className="notion-prop-name">
        {typeof column.columnDef.header === "function"
          ? column.columnDef.header(column.getContext())
          : column.columnDef.header}
      </div>
      <div className="notion-prop-toggle" onClick={() => !alwaysVisible && column.toggleVisibility()}>
        {column.getIsVisible() ? <MdVisibility size={20} /> : <MdVisibilityOff size={20} />}
      </div>
    </div>
  );
};

const Sessions = ({ dashboardData }) => {
  const sessions = dashboardData ? dashboardData.sessions : [];
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [toggleAllVisible, setToggleAllVisible] = useState(true);
  const searchInputRef = useRef(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [showDeleteSession, setShowDeleteSession] = useState(false);

  const data = useMemo(() =>
    sessions.map((session, index) => ({
      sno: index + 1,
      id: session.session_id,
      racetrack: session.racetrack,
      date: new Date(session.date).toLocaleDateString(),
      championship: session.championship,
      lap_count: session.lap_count,
      best_lap_time: session.best_lap_time,
      session_type: session.session_type,
      vehicle: session.vehicle,
      driver: session.driver,
      filename: session.filename.substring(0, session.filename.lastIndexOf('.')) || session.filename,
    })),
    [sessions]
  );

  const columns = useMemo(() => [
    {
      id: "select-col",
      header: ({ table }) => (
        <IndeterminateCheckbox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 50,
    },
    { accessorKey: "racetrack", header: "Racetrack", meta: { filterVariant: "text" }, size: 150},
    { accessorKey: "date", header: "Date", meta: { filterVariant: "text" }, size: 90 },
    { accessorKey: "championship", header: "Championship", meta: { filterVariant: "text" }, size: 150 },
    { accessorKey: "lap_count", header: "Laps", meta: { filterVariant: "text" }, size: 50 },
    { accessorKey: "best_lap_time", header: "Best time", meta: { filterVariant: "text" }, size: 90 },
    { accessorKey: "session_type", header: "Type", meta: { filterVariant: "text" }, size: 90 },
    { accessorKey: "vehicle", header: "Vehicle", meta: { filterVariant: "text" }, size: 150 },
    { accessorKey: "driver", header: "Driver", meta: { filterVariant: "text" }, size: 150 },
    { accessorKey: "filename", header: "Filename", meta: { filterVariant: "text" }, size: 150 },
  ], []);

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      rowSelection,
      sorting,
      columnVisibility,
      columnOrder,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: {
      pagination: {
        pageSize: 13,
      },
    },
  });

  useEffect(() => {
    const saved = localStorage.getItem('sessions_table_state');
    if (saved) {
      const parsed = JSON.parse(saved);
      setColumnOrder(parsed.columnOrder || []);
      setColumnVisibility(parsed.columnVisibility || {});
      setSorting(parsed.sorting || []);
      setColumnFilters(parsed.columnFilters || []);
      setShowSearchInput(parsed.showSearchInput ?? false);
      setShowColumnFilters(parsed.showColumnFilters ?? false);
      if (parsed.pageIndex !== undefined) {
        table.setPageIndex(parsed.pageIndex);
      }
    }
  }, []);

  useEffect(() => {
    const stateToPersist = {
      columnOrder,
      columnVisibility,
      sorting,
      columnFilters,
      pageIndex: table.getState().pagination.pageIndex,
      showSearchInput,
      showColumnFilters,
    };
    localStorage.setItem('sessions_table_state', JSON.stringify(stateToPersist));
  }, [
    columnOrder,
    columnVisibility,
    sorting,
    columnFilters,
    showSearchInput,
    showColumnFilters,
    table.getState().pagination.pageIndex,
  ]);
  

  const moveItem = (dragIndex, hoverIndex) => {
    const orderedCols = table.getAllLeafColumns().filter(col => col.id !== "select-col");
    const ids = orderedCols.map(c => c.id);
    const [moved] = ids.splice(dragIndex, 1);
    ids.splice(hoverIndex, 0, moved);
    setColumnOrder(["select-col", ...ids]);
  };

  const toggleAllColumns = () => {
    const newState = {};
    table.getAllLeafColumns().forEach((col) => {
      if (col.id !== "select-col") newState[col.id] = toggleAllVisible;
    });
    setColumnVisibility(newState);
    setToggleAllVisible(!toggleAllVisible);
  };

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  const handleDeleteSessions = () => {
    if (!Object.keys(rowSelection).length) {
      toast.error("Please select at least one session to delete.");
      return;
    }
    setShowDeleteSession(true);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="sessions">
        <h1>Sessions</h1>
        <div className="mainContainer">
          <div className="filterContainer">
            <div className="searchHeader">
              <div className="search-controls">
                <div className={`search-wrapper ${showSearchInput ? 'expanded' : ''}`}>
                  <button
                    className={`search-button ${showSearchInput ? "active" : ""}`}
                    onClick={() => setShowSearchInput((prev) => !prev)}
                  >
                    <MdSearch size={22}/>
                  </button>
                  {showSearchInput && (
                    <input
                      ref={searchInputRef}
                      type="search"
                      value={globalFilter ?? ""}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      placeholder="Search in all columns..."
                      className="search-input"
                    />
                  )}
                </div>

                <button
                  title="Toggle column filters"
                  className={`icon-button ${showColumnFilters ? "active" : ""}`}
                  onClick={() => {
                    setShowColumnFilters((prev) => {
                      if (prev) {
                        setColumnFilters([]);
                        table.resetColumnFilters();
                      }
                      return !prev;
                    });
                  }}
                >
                  <MdFilterList size={22}/>
                </button>

                <div className="column-menu-anchor" style={{ position: "relative" }}>
                  <button
                    title="Columns"
                    className={`icon-button ${showColumnMenu ? "active" : ""}`}
                    onClick={() => setShowColumnMenu(prev => !prev)}
                  >
                    <MdFormatListBulleted  size={22}/>
                  </button>

                  {showColumnMenu && (
                    <div className="notion-properties-popup">
                      <div className="notion-popup-header">
                        <strong>Shown in table</strong>
                        <button onClick={toggleAllColumns}>
                          {toggleAllVisible ? "Hide all" : "Show all"}
                        </button>
                      </div>
                      <div className="notion-popup-body">
                        {table.getAllLeafColumns()
                          .filter(col => col.id !== "select-col")
                          .map((col, index) => (
                            <DraggableColumnItem
                              key={col.id}
                              column={col}
                              index={index}
                              moveItem={moveItem}
                              alwaysVisible={false}
                            />
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="cont-btn">
                <button className="btn-generate" onClick={() => setShowGenerateReport(true)}>
                  <MdDescription size={22}/>
                  Generate report
                </button>
                <button className="btn-session" onClick={() => setShowAddSession(true)}>
                  <MdAddCircleOutline size={22}/>
                  Add session
                </button>
                <button className="basura" onClick={handleDeleteSessions}>
                  <MdDelete size={22}/>
                </button>
              </div>
            </div>
            <div className="tableContainer">
              <table id="itemsTable">
                <thead className="table-header">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th 
                          key={header.id} 
                          colSpan={header.colSpan}
                          style={{ width: `${header.getSize()}px` }}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{ asc: " ↑", desc: " ↓" }[header.column.getIsSorted()] ?? null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                  {showColumnFilters && (
                    <tr>
                      {table.getHeaderGroups()[0].headers.map((header) => (
                        <th key={header.id}>
                          {header.column.getCanFilter() ? (
                            <RenderColumnFilter column={header.column} />
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length}>No sessions found.</td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} title={cell.getValue()}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="pagination">
                <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                  <IoIosArrowBack size={20}/>
                </button>
                <span>
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </span>
                <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                  <IoIosArrowForward size={20}/>
                </button>
              </div>
            </div>
          </div>
        </div>

        {showGenerateReport && <PopupReport dashboardData={dashboardData} rowSelection={rowSelection} onClose={() => setShowGenerateReport(false)} />}
        {showAddSession && <PopupSession dashboardData={dashboardData} onClose={() => setShowAddSession(false)} />}
        {showDeleteSession && <PopupDeleteSession rowSelection={rowSelection} setRowSelection={setRowSelection} onClose={() => setShowDeleteSession(false)} />}
      </div>
    </DndProvider>
  );
};

export default Sessions;
